// ============================================================================
// VOWBOT MESSAGE CARD COMPONENTS
// ============================================================================
// Rich UI components for displaying structured VowBot responses
// Uses formatters from utils/vowbotFormatters.js
// ============================================================================

import React from 'react';
import {
  formatVendorCard,
  formatProgressCard,
  formatTimelineCard,
  getMatchBadgeColor,
  formatBudgetDisplay,
  getUrgencyIcon,
  getProgressStatus,
  formatRelativeDate
} from '../utils/vowbotFormatters';

// ============================================================================
// VENDOR CARD COMPONENT
// ============================================================================

export const VendorCard = ({ vendor, onViewProfile }) => {
  const formatted = formatVendorCard(vendor);
  
  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 border-2 border-rose-200/50 shadow-lg hover:shadow-xl transition-all duration-300 mb-3">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-bold text-gray-800 text-lg">{formatted.name}</h4>
          <p className="text-sm text-gray-600 capitalize">{formatted.category}</p>
        </div>
        {formatted.hasThemeMatch && (
          <div className={`${getMatchBadgeColor(formatted.themeMatch)} text-white px-3 py-1 rounded-full text-xs font-bold`}>
            {formatted.themeMatch}% {formatted.matchBadge}
          </div>
        )}
      </div>
      
      {/* Details */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center text-sm text-gray-700">
          <span className="mr-2">📍</span>
          <span>{formatted.location}</span>
        </div>
        <div className="flex items-center text-sm text-gray-700">
          <span className="mr-2">⭐</span>
          <span>{formatted.rating}/5</span>
        </div>
        <div className="flex items-center text-sm text-gray-700">
          <span className="mr-2">💰</span>
          <span>{formatted.priceRange}</span>
        </div>
      </div>
      
      {/* Match Reasons */}
      {formatted.matchReasons && formatted.matchReasons.length > 0 && (
        <div className="bg-rose-50 rounded-lg p-3 mb-3">
          <p className="text-xs font-semibold text-rose-700 mb-1">Why this matches:</p>
          {formatted.matchReasons.slice(0, 2).map((reason, idx) => (
            <p key={idx} className="text-xs text-gray-700 flex items-start">
              <span className="mr-1">✨</span>
              <span>{reason}</span>
            </p>
          ))}
        </div>
      )}
      
      {/* Action Button */}
      <button 
        onClick={() => onViewProfile ? onViewProfile(vendor) : window.open('/vendor-vault/vendor-shortlisting-assistant', '_blank')}
        className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white py-2 px-4 rounded-lg font-semibold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300"
      >
        View Profile
      </button>
    </div>
  );
};

// ============================================================================
// PROGRESS CARD COMPONENT
// ============================================================================

export const ProgressCard = ({ context }) => {
  // Backend already sends formatted data, use it directly
  const formatted = context.overall !== undefined ? context : formatProgressCard(context);
  
  if (!formatted) return null;
  
  const status = getProgressStatus(formatted.overall);
  
  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 border-2 border-purple-200/50 shadow-lg mb-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold text-gray-800 text-lg flex items-center">
          <span className="mr-2">📊</span>
          Your Progress
        </h4>
        <span className={`${status.color} font-bold text-sm`}>{status.label}</span>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700">Overall</span>
          <span className="text-sm font-bold text-purple-600">{formatted.overall}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${formatted.overall}%` }}
          ></div>
        </div>
      </div>
      
      {/* Breakdown */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white/60 rounded-lg p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">🎨 Theme</span>
            <span className="text-xs font-bold">{formatted.breakdown.theme}%</span>
          </div>
        </div>
        <div className="bg-white/60 rounded-lg p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">📅 Date</span>
            <span className="text-xs font-bold">{formatted.breakdown.date}%</span>
          </div>
        </div>
        <div className="bg-white/60 rounded-lg p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">💰 Budget</span>
            <span className="text-xs font-bold">{formatted.breakdown.budget}%</span>
          </div>
        </div>
        <div className="bg-white/60 rounded-lg p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">🏪 Vendors</span>
            <span className="text-xs font-bold">{Math.round(formatted.breakdown.vendors)}%</span>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="flex items-center justify-around py-3 border-t border-purple-200">
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-600">{formatted.counts.themes}</p>
          <p className="text-xs text-gray-600">Themes</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-pink-600">{formatted.counts.vendors}</p>
          <p className="text-xs text-gray-600">Vendors</p>
        </div>
        {formatted.counts.daysUntil && (
          <div className="text-center">
            <p className="text-2xl font-bold text-rose-600 flex items-center justify-center">
              {getUrgencyIcon(formatted.counts.daysUntil)}
              <span className="ml-1">{formatted.counts.daysUntil}</span>
            </p>
            <p className="text-xs text-gray-600">Days Left</p>
          </div>
        )}
      </div>
      
      {/* Gaps */}
      {formatted.gaps && formatted.gaps.length > 0 && (
        <div className="mt-4 pt-4 border-t border-purple-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">Still need to:</p>
          <div className="flex flex-wrap gap-2">
            {formatted.gaps.map((gap, idx) => {
              // Map gaps to action URLs
              const gapActions = {
                'Choose theme': '/wedding-blueprint/dream-wedding-generator',
                'Create Theme': '/wedding-blueprint/dream-wedding-generator',
                'Set date': '/dashboard',
                'Set Date': '/dashboard',
                'Set budget': '/wedding-blueprint/budget-allocator',
                'Set Budget': '/wedding-blueprint/budget-allocator',
                'Find vendors': '/vendor-vault/smart-vendor-matching',
                'Find Vendors': '/vendor-vault/smart-vendor-matching'
              };
              
              const actionUrl = gapActions[gap.label];
              
              return (
                <button
                  key={idx}
                  onClick={() => actionUrl && window.open(actionUrl, '_blank')}
                  className={`bg-white/80 px-3 py-1 rounded-full text-xs font-semibold text-gray-700 border border-purple-200 ${
                    actionUrl ? 'hover:bg-purple-100 hover:border-purple-300 cursor-pointer transition-all duration-200' : ''
                  }`}
                >
                  {gap.icon} {gap.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Dashboard Button */}
      <div className="mt-3">
        <button
          onClick={() => window.open('/dashboard', '_blank')}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-3 rounded-lg font-semibold text-xs hover:shadow-md transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-1"
        >
          <span>📊</span>
          <span>View Dashboard</span>
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// TIMELINE CARD COMPONENT
// ============================================================================

export const TimelineCard = ({ tasks, daysUntil }) => {
  const formatted = formatTimelineCard(tasks, daysUntil);
  
  if (!formatted) return null;
  
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border-2 border-blue-200/50 shadow-lg mb-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold text-gray-800 text-lg flex items-center">
          <span className="mr-2">📅</span>
          Timeline Tasks
        </h4>
        {formatted.daysUntil && (
          <span className="text-sm font-semibold text-blue-600">
            {formatRelativeDate(formatted.daysUntil)} to go
          </span>
        )}
      </div>
      
      {/* Tasks */}
      <div className="space-y-3">
        {formatted.tasks.map((task, idx) => (
          <div 
            key={idx} 
            className={`bg-white/80 rounded-lg p-3 border-l-4 ${
              task.urgency === 'urgent' ? 'border-red-500' :
              task.urgency === 'soon' ? 'border-yellow-500' :
              'border-blue-500'
            }`}
          >
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center flex-1">
                <span className="mr-2">{task.icon}</span>
                <h5 className="font-semibold text-gray-800 text-sm">{task.title}</h5>
              </div>
              {task.urgency === 'urgent' && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-bold">
                  URGENT
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 ml-6">{task.description}</p>
            {task.suggestedDate && (
              <p className="text-xs text-blue-600 ml-6 mt-1">
                📌 {task.suggestedDate}
              </p>
            )}
          </div>
        ))}
      </div>
      
      {/* Footer */}
      {formatted.totalTasks > formatted.tasks.length && (
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-600">
            Showing {formatted.tasks.length} of {formatted.totalTasks} tasks
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  VendorCard,
  ProgressCard,
  TimelineCard
};
